'use client';

import { useEffect, useRef } from 'react';
import gsap from 'gsap';

/**
 * The signal-flow animation for the Pulse-Omics graph.
 *
 * Each chord is a wire between two data points that passes through the pulse at
 * the centre. A signal is a short glowing pulse that enters at one point,
 * travels the wire, and arrives at the other. A few are in flight at once,
 * never many, so any single one can be followed by eye.
 *
 * One firing, in order:
 *   1. the origin node's dot flares, scaling up and brightening
 *   2. a pulse departs and travels the chord, passing through the centre
 *   3. the hub gives a barely-perceptible tick as it goes by
 *   4. the destination dot flares as it lands
 *   5. the slot rests, then fires again on a different chord
 *
 * Two decisions worth keeping:
 *
 * Chord choice comes from a shuffle bag, not Math.random() per pick. A bag
 * guarantees all twenty fire before any repeats, which is what "random" is
 * actually being asked for here — raw random clumps, replaying the same two or
 * three chords while others sit dead for a while.
 *
 * Duration is per chord and not derived from its length, so a long chord takes
 * the same unhurried time as a short one. Speed-matching would make the long
 * ones whip and the short ones crawl.
 */

/** Concurrent signals. Enough to feel busy, few enough to follow any one. */
const SLOTS = 6;
/** Seconds for a full traversal, picked per firing. */
const TRAVEL = [3.4, 4.8] as const;
/** Seconds a slot rests between firings. Kept short to lift the firing rate. */
const REST = [0.35, 1.4] as const;
/**
 * Length of the bright head, in path units. Short: the signal should read as a
 * travelling pulse, not a stripe being dragged along the wire. Note the blur on
 * the soft tier visually extends it, so raising this reads longer than the
 * number suggests.
 */
const DASH = 34;

/**
 * How far a chord's own opacity is lifted while a signal is on it, against its
 * resting value. The resting values are the designer's three tiers, so this
 * multiplies rather than replaces — a faint wire brightens by as much as a
 * strong one, proportionally, and the tiers survive the lift.
 */
const BASE_LIFT = 2.1;

/**
 * Roughly this share of firings are drawn soft: blurred, a little dimmer, a
 * touch slower. Light enough to give the bundle depth without pulling the
 * crisp signals back into the noise.
 */
const SOFT_SHARE = 0.32;
const SOFT_OPACITY = 0.58;
const SOFT_SLOWDOWN = 1.12;

const rand = ([lo, hi]: readonly [number, number]) =>
  lo + Math.random() * (hi - lo);

/** Fisher-Yates, so the bag order is uniform rather than sort-comparator luck. */
const shuffled = (n: number) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

export type SignalFlowRefs = {
  /**
   * The resting wires themselves. A signal lifts the one it is travelling
   * before dropping it back, so the chord reads as carrying something rather
   * than as a bright dash sliding over an inert line.
   */
  base: (SVGPathElement | null)[];
  core: (SVGPathElement | null)[];
  glow: (SVGPathElement | null)[];
  dots: (SVGCircleElement | null)[];
  hub: SVGCircleElement | null;
  /** from/to node index per chord, in the same order as core/glow. */
  ends: { from: number; to: number }[];
};

export type SignalFlowOptions = {
  /**
   * Whether this instance should run. Both the desktop and the mobile ring are
   * always in the DOM — CSS decides which is visible — so the one that is
   * hidden is switched off here rather than left animating unseen.
   */
  enabled?: boolean;
  /** Dot radius at rest, in the variant's own design units. */
  dotR?: number;
  /** Hub radius at rest, in the variant's own design units. */
  hubR?: number;
  /**
   * The blur filter this instance's soft signals reference. Each variant
   * carries its own <defs>, so the ids have to differ or the two SVGs collide
   * on one document-wide id.
   */
  softFilterId?: string;
};

export const useSignalFlow = (
  refs: React.MutableRefObject<SignalFlowRefs>,
  rootRef: React.RefObject<Element | null>,
  {
    enabled = true,
    dotR = 3.17834,
    hubR = 36,
    softFilterId = 'po-signal-soft',
  }: SignalFlowOptions = {},
) => {
  const tweens = useRef<gsap.core.Timeline[]>([]);
  const bag = useRef<number[]>([]);
  const busy = useRef<Set<number>>(new Set());
  const running = useRef(false);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !enabled) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduce.matches) return undefined;

    const { base, core, glow, dots, hub, ends } = refs.current;

    /* Each wire's own resting opacity, read once off the DOM so the lift can
       return it exactly rather than to a shared guess. */
    const rest = base.map((el) => {
      const v = el?.getAttribute('stroke-opacity');
      return v ? parseFloat(v) : 1;
    });
    const count = core.length;
    if (!count) return undefined;

    const lengths = core.map((p) => (p ? p.getTotalLength() : 0));

    /* Park every signal off the end of its path so nothing shows at rest. */
    core.forEach((p, i) => {
      if (!p) return;
      const l = lengths[i];
      gsap.set([p, glow[i]], {
        strokeDasharray: `${DASH} ${l}`,
        strokeDashoffset: DASH,
        opacity: 0,
      });
    });

    /** Take the next chord from the bag, skipping any already in flight. */
    const nextChord = (): number | null => {
      for (let guard = 0; guard < count * 2; guard += 1) {
        if (!bag.current.length) bag.current = shuffled(count);
        const c = bag.current.pop();
        if (c === undefined) return null;
        if (!busy.current.has(c)) return c;
        /* In flight: put it back at the bottom rather than dropping it, so the
           bag still covers every chord before repeating. */
        bag.current.unshift(c);
      }
      return null;
    };

    const fire = (slot: number) => {
      if (!running.current) return;
      const c = nextChord();
      if (c === null) return;

      const l = lengths[c];
      const path = core[c];
      const halo = glow[c];
      if (!path || !l) return;

      busy.current.add(c);
      const forward = Math.random() < 0.5;
      const soft = Math.random() < SOFT_SHARE;
      const dur = rand(TRAVEL) * (soft ? SOFT_SLOWDOWN : 1);
      const peak = soft ? SOFT_OPACITY : 1;

      /* Depth is set per firing rather than per chord, so the same wire reads
         near one pass and far the next. */
      [path, halo].forEach((el) => {
        /* halo is only null-checked here: `path` is guarded above because the
           tween needs it, but its glow twin can be absent on its own. */
        if (!el) return;
        if (soft) el.setAttribute('filter', `url(#${softFilterId})`);
        else el.removeAttribute('filter');
      });
      const originDot = dots[forward ? ends[c].from : ends[c].to];
      const targetDot = dots[forward ? ends[c].to : ends[c].from];

      const tl = gsap.timeline({
        onComplete: () => {
          busy.current.delete(c);
          gsap.set([path, halo], { opacity: 0 });
          if (running.current) {
            gsap.delayedCall(rand(REST), () => fire(slot));
          }
        },
      });

      /* The wire lifts under the signal and settles after it. Timed to the
         travel rather than run as its own loop, so the two cannot drift. */
      const wire = base[c];
      if (wire) {
        const lifted = Math.min(1, rest[c] * BASE_LIFT);
        tl.to(
          wire,
          {
            attr: { 'stroke-opacity': lifted },
            duration: 0.4,
            ease: 'sine.out',
          },
          0.05,
        ).to(
          wire,
          {
            attr: { 'stroke-opacity': rest[c] },
            duration: 0.7,
            ease: 'sine.inOut',
          },
          0.05 + dur - 0.5,
        );
      }

      /* Origin flare leads the departure slightly, so the node looks like the
         source rather than something the pulse happens to pass. */
      if (originDot) {
        tl.to(
          originDot,
          { attr: { r: dotR * 1.89 }, duration: 0.22, ease: 'power2.out' },
          0,
        ).to(
          originDot,
          { attr: { r: dotR }, duration: 0.5, ease: 'power2.inOut' },
          0.22,
        );
      }

      /* The travel itself. Ease in and out so it leaves with a little
         acceleration and settles in rather than stopping dead. */
      tl.to(
        [path, halo],
        { opacity: peak, duration: 0.35, ease: 'none' },
        0.05,
      );
      tl.fromTo(
        [path, halo],
        { strokeDashoffset: forward ? DASH : -l },
        {
          strokeDashoffset: forward ? -l : DASH,
          duration: dur,
          ease: 'power1.inOut',
        },
        0.05,
      );
      tl.to(
        [path, halo],
        { opacity: 0, duration: 0.45, ease: 'none' },
        0.05 + dur - 0.45,
      );

      /* Hub tick as the signal passes the centre. Deliberately slight: it is a
         confirmation, not an event. */
      if (hub) {
        tl.to(
          hub,
          { attr: { r: hubR * 1.07 }, duration: 0.3, ease: 'sine.out' },
          0.05 + dur * 0.5,
        ).to(
          hub,
          { attr: { r: hubR }, duration: 0.6, ease: 'sine.inOut' },
          0.05 + dur * 0.5 + 0.3,
        );
      }

      /* Arrival flare. */
      if (targetDot) {
        tl.to(
          targetDot,
          { attr: { r: dotR * 1.89 }, duration: 0.22, ease: 'power2.out' },
          0.05 + dur - 0.16,
        ).to(
          targetDot,
          { attr: { r: dotR }, duration: 0.5, ease: 'power2.inOut' },
          0.05 + dur + 0.06,
        );
      }

      tweens.current.push(tl);
    };

    const start = () => {
      if (running.current) return;
      running.current = true;
      /* Stagger the first firings so they do not all launch on frame one. */
      for (let s = 0; s < SLOTS; s += 1) {
        gsap.delayedCall(s * 0.6 + Math.random() * 0.5, () => fire(s));
      }
    };

    const stop = () => {
      running.current = false;
      gsap.globalTimeline.getChildren(true, true, true).forEach((t) => {
        /* Only our delayedCalls and timelines; other sections' tweens are not
           in this list because they are killed with their own components. */
        if (tweens.current.includes(t as gsap.core.Timeline)) t.kill();
      });
      tweens.current.forEach((t) => t.kill());
      tweens.current = [];
      busy.current.clear();
      core.forEach((p, i) => {
        if (p) gsap.set([p, glow[i]], { opacity: 0, strokeDashoffset: DASH });
      });
      base.forEach(
        (el, i) => el && gsap.set(el, { attr: { 'stroke-opacity': rest[i] } }),
      );
      dots.forEach((d) => d && gsap.set(d, { attr: { r: dotR } }));
      if (hub) gsap.set(hub, { attr: { r: hubR } });
    };

    /* Only run while the section is on screen. A carousel of signals cycling
       unseen is wasted frames on a page this long. */
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => (e.isIntersecting ? start() : stop())),
      { threshold: 0.15 },
    );
    io.observe(root);

    return () => {
      io.disconnect();
      stop();
    };
  }, [refs, rootRef, enabled, dotR, hubR, softFilterId]);
};
